import _ from 'lodash';

// The only values the employee-type filter accepts (from user_attributes).
export const EMPLOYEE_TYPES = ['H', 'S'];

const isEmail = (value) => String(value).includes('@');

/**
 * The empty form-state shape. Store filters are plural arrays of strings;
 * employee_types holds 'H' / 'S'; specific_users holds social-champ emails
 * and/or WINs (split into social_champ_emails / social_champ_wins for the API).
 */
export function initTargeting() {
  return {
    store_types: [],
    states: [],
    regions: [],
    markets: [],
    store_numbers: [],
    employee_types: [],
    specific_users: [],
  };
}

/**
 * Maps the persisted `task.targeting` JSONB (or its already-parsed object) onto
 * the form-state shape.
 */
export function targetingFromAPI(targeting) {
  if (!targeting) return initTargeting();

  const obj = _.isString(targeting) ? JSON.parse(targeting) : targeting;

  return {
    store_types: obj.store_type || [],
    states: obj.state || [],
    regions: obj.region || [],
    markets: obj.market || [],
    store_numbers: obj.store_number || [],
    employee_types: _.get(obj, 'user.employee_type') || [],
    specific_users: [
      ...(_.get(obj, 'user.social_champ_emails') || []),
      ...(_.get(obj, 'user.social_champ_wins') || []),
    ],
  };
}

/**
 * Trims and de-dupes free-text filter values, and drops any employee type that
 * isn't a known enum value.
 */
export function trimTargeting(targeting = {}) {
  const clean = (values) =>
    _.uniq(_.compact((values || []).map((value) => String(value).trim())));

  return {
    store_types: clean(targeting.store_types),
    states: clean(targeting.states),
    regions: clean(targeting.regions),
    markets: clean(targeting.markets),
    store_numbers: clean(targeting.store_numbers),
    specific_users: clean(targeting.specific_users),
    employee_types: (targeting.employee_types || []).filter((value) =>
      EMPLOYEE_TYPES.includes(value)
    ),
  };
}

/**
 * Maps the form-state shape onto the API `targeting` shape - flat store keys
 * plus a nested `user` object - dropping every empty filter. `specific_users`
 * is partitioned into champ emails vs. WINs by the presence of an "@".
 */
export function targetingToAPI(targeting = {}) {
  const trimmed = trimTargeting(targeting);

  const user = _.omitBy(
    {
      employee_type: trimmed.employee_types,
      social_champ_emails: trimmed.specific_users.filter(isEmail),
      social_champ_wins: trimmed.specific_users.filter((v) => !isEmail(v)),
    },
    _.isEmpty
  );

  const obj = {
    store_type: trimmed.store_types,
    state: trimmed.states,
    region: trimmed.regions,
    market: trimmed.markets,
    store_number: trimmed.store_numbers,
    ...(_.isEmpty(user) ? {} : { user }),
  };

  return _.omitBy(obj, _.isEmpty);
}

/** True when no filter is set - the task is visible to everyone. */
export function isTargetingEmpty(targeting) {
  return _.isEmpty(targetingToAPI(targeting || {}));
}
